import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  useApplicationDetails,
  useApplicationMessages,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import pluralize from 'pluralize'
import { useAuth } from 'app/frontend/base/config/context/auth'
import ApplicationMessages from './ApplicationMessages'
import { cn } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Props = {
  type: 'messages' | 'notes'
  applicationIdProp?: number
  team?: wowaudit.Team
}

const ApplicationMessagesDialog = ({
  type,
  applicationIdProp,
  team,
}: Props) => {
  const { applicationId: applicationIdParam } = useParams()
  const applicationId = applicationIdProp ?? parseInt(applicationIdParam ?? '0')
  const queryClient = useQueryClient()

  const memberVisibilityLevel = team
    ? type === 'messages'
      ? team.settings.recruitmentMessagesVisibility.value
      : team.settings.recruitmentInternalNotesVisibility.value
    : 'full_access'

  const {
    data: { user },
  } = usePageInfo()
  const { accessLevel } = useAuth()
  const [messageDialogOpen, setMessageDialogOpen] = useState(false)
  const {
    data: applicationDetails,
    refetch,
    isPending,
  } = useApplicationDetails(team?.id, applicationId ? applicationId : undefined)
  const isApplicant = applicationDetails?.userReference?.id === user?.id
  const canViewInternalNotes =
    type !== 'notes' ||
    accessLevel === 'admin' ||
    (!!applicationDetails && !isApplicant)
  const canViewDialog =
    accessLevel === 'admin' ||
    (memberVisibilityLevel !== 'none' && canViewInternalNotes)
  const { data: messages } = useApplicationMessages(
    messageDialogOpen,
    type,
    team?.id,
    canViewDialog && applicationDetails ? applicationId : undefined,
  )

  const lastRead =
    applicationDetails?.timestamps?.[
      type === 'messages' ? 'read_messages_at' : 'read_notes_at'
    ]

  const firstUnreadIndex = messages?.findIndex((message) =>
    lastRead ? dayjs(message.createdAt).isAfter(dayjs(lastRead)) : true,
  )

  const amountOfUnreadMessages =
    firstUnreadIndex === undefined || firstUnreadIndex === -1
      ? 0
      : (messages?.filter((m) =>
          type === 'messages' ? !m.internal : m.internal,
        )?.length ?? 0) - firstUnreadIndex

  return (
    <Dialog
      open={messageDialogOpen}
      onOpenChange={(open) => {
        setMessageDialogOpen(open)
        if (!open) {
          refetch()
          queryClient.invalidateQueries({
            queryKey: ['userApplicationSummary'],
          })
        }
      }}
    >
      {canViewDialog ? (
        <DialogTrigger asChild>
          <Button
            variant='outline'
            loading={isPending}
            className={cn(
              'w-36',
              amountOfUnreadMessages > 0 ? 'border-warning-foreground' : '',
            )}
          >
            {amountOfUnreadMessages > 0 ? (
              <span className='text-warning-foreground relative w-full'>
                {amountOfUnreadMessages} new{' '}
                {pluralize(
                  type === 'messages' ? 'message' : 'note',
                  amountOfUnreadMessages,
                )}
                <span className='absolute top-[-0.8rem] right-[-1.3rem] w-3 h-3 bg-destructive-foreground rounded-full' />
              </span>
            ) : accessLevel === 'admin' ||
              applicationDetails?.userReference?.id === user?.id ||
              memberVisibilityLevel === 'full_access' ? (
              <span>{type === 'messages' ? 'Send message' : 'Add note'}</span>
            ) : (
              <span>{type === 'messages' ? 'Messages' : 'Notes'}</span>
            )}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent
        className='max-w-2xl h-[70vh] flex flex-col p-0 gap-0'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className='p-4 pb-0'>
          <DialogTitle className='flex items-center gap-2'>
            <span>{type === 'messages' ? 'Messages' : 'Internal notes'}</span>
            {type === 'notes' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 text-muted-foreground' />
                </TooltipTrigger>
                <TooltipContent>
                  Notes are never visible to the applicant, even after they join
                  the team
                </TooltipContent>
              </Tooltip>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className='flex-1 pl-6 pr-0 py-4 min-h-0'>
          {canViewDialog && applicationId && (
            <ApplicationMessages
              applicationId={applicationId}
              type={type}
              lastRead={lastRead}
              onNewMessage={refetch}
              teamId={team?.id}
              memberReadonly={memberVisibilityLevel === 'readonly'}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ApplicationMessagesDialog
